"use client"

import { useState, useEffect } from "react"
import styles from "./servicios-legales-section.module.css"

const ServiciosLegalesSection = () => {
  const [activeSection, setActiveSection] = useState("intro")
  const [visiblePortfolioCards, setVisiblePortfolioCards] = useState([])
  const [showPortfolioArrow, setShowPortfolioArrow] = useState(false)

  const [openCollapsibles, setOpenCollapsibles] = useState({
    quienes: false,
    derechos: false,
    donde: false,
    queEsCuotaLitis: false,
    aspectosClave: false,
    nuestroCompromiso: false,
  })

  useEffect(() => {
    const animateCuotaLitis = () => {
      const animatedBoxes = document.querySelectorAll(`.${styles.animatedBox}`)

      animatedBoxes.forEach((box, index) => {
        setTimeout(
          () => {
            box.classList.add(styles.slideIn)
          },
          300 + index * 300,
        )
      })
    }

    const modeloObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            animateCuotaLitis()
            modeloObserver.disconnect()
          }
        })
      },
      { threshold: 0.1 },
    )

    const modeloSection = document.getElementById("legal-modelo")
    if (modeloSection) {
      modeloObserver.observe(modeloSection)
    }

    return () => {
      if (modeloSection) {
        modeloObserver.disconnect()
      }
    }
  }, [])

  useEffect(() => {
    const animatePortfolioCards = () => {
      const cards = document.querySelectorAll(`.${styles.portfolioCard}`)
      cards.forEach((card, index) => {
        setTimeout(() => {
          setVisiblePortfolioCards((prev) => [...prev, index])
        }, index * 400)
      })
    }

    const portfolioObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.target.id === "legal-portafolio") {
            setVisiblePortfolioCards([])
            setTimeout(() => {
              animatePortfolioCards()
            }, 100)
            setShowPortfolioArrow(true)
          } else if (!entry.isIntersecting && entry.target.id === "legal-portafolio") {
            setVisiblePortfolioCards([])
            setShowPortfolioArrow(false)
          }
        })
      },
      { threshold: 0.2 },
    )

    const portfolioSection = document.getElementById("legal-portafolio")

    if (portfolioSection) {
      portfolioObserver.observe(portfolioSection)
    }

    return () => {
      portfolioObserver.disconnect()
    }
  }, [])

  const scrollToSection = (sectionId) => {
    setActiveSection(sectionId)
    const element = document.getElementById(`legal-${sectionId}`)
    if (element) {
      element.scrollIntoView({ behavior: "smooth" })
    }
  }

  const scrollToPortfolioTitle = () => {
    const element = document.getElementById("portfolio-title")
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" })
    }
  }

  const handleGoContact = () => {
    const phoneNumber = "573175789585"
    const defaultMessage = "Hola, estoy interesado en recibir asesoría legal."
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(defaultMessage)}`
    window.open(whatsappUrl, "_blank")
  }

  const toggleCollapsible = (key) => {
    setOpenCollapsibles((prev) => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  const especialidades = [
    {
      title: "Derecho Financiero",
      desc: "Asesoramos en regulación bancaria, mercados de capitales, estructuración de financiación, cumplimiento normativo y otros aspectos cruciales del sector financiero.",
    },
    {
      title: "Derecho Constitucional",
      desc: "Ofrecemos análisis y representación en asuntos relacionados con la protección de derechos fundamentales, acciones de tutela y control de constitucionalidad.",
    },
    {
      title: "Derecho Civil",
      desc: "Brindamos asesoría y representación en obligaciones y contratos civiles, responsabilidad civil, procesos de pertenencia y sucesiones.",
    },
    {
      title: "Derecho Comercial",
      desc: "Apoyamos en constitución de sociedades, contratos mercantiles, títulos valores, competencia desleal y propiedad industrial.",
    },
    {
      title: "Derecho Administrativo",
      desc: "Representación en relaciones con el Estado, contratación estatal, procedimientos administrativos y responsabilidad estatal.",
    },
    {
      title: "Derecho Tributario",
      desc: "Consultoría en planificación fiscal, cumplimiento de obligaciones tributarias y representación ante autoridades fiscales.",
    },
  ]

  const ChevronDown = ({ isOpen }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${styles.chevronIcon} ${isOpen ? styles.chevronOpen : ""}`}
    >
      <polyline points="6,9 12,15 18,9"></polyline>
    </svg>
  )

  return (
    <div className={styles.legalServicesSection}>
      {/* Flecha flotante para volver al título del portafolio */}
      {showPortfolioArrow && (
        <button className={styles.portfolioArrowBtn} onClick={scrollToPortfolioTitle}>
          ↑
        </button>
      )}

      {/* Título principal de la sección */}
      <div className={styles.sectionHeader}>
        <p className={styles.sectionDescription}>
          Asesoría Legal Integral para Proteger sus Intereses y Potenciar su Crecimiento
        </p>
      </div>

      {/* Navegación de secciones */}
      <div className={styles.legalNavHorizontal}>
        {[
          { id: "intro", label: "Introducción" },
          { id: "portafolio", label: "Portafolio Principal" },
          { id: "especializacion", label: "Especialización" },
          { id: "modelo", label: "Cuota litis" },
        ].map((item) => (
          <button
            key={item.id}
            className={`${styles.navBtnHorizontal} ${activeSection === item.id ? styles.active : ""}`}
            onClick={() => scrollToSection(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Contenido principal */}
      <div className={styles.legalContentIntegrated}>
        {/* Portafolio Principal con animaciones */}
        <section id="legal-portafolio" className={styles.legalSection}>
          <div className={styles.sectionHeader}>
            <h3 id="portfolio-title" className={styles.portfolioTitle}>
              Defensa del Consumidor Financiero
            </h3>
          </div>
          <div className={styles.portafolioContent}>
            {/* Tarjeta 1: Defensa del Consumidor Financiero */}
            <div
              className={`${styles.serviceDetail} ${styles.portfolioCard} ${styles.dynamicCard} ${
                visiblePortfolioCards.includes(0) ? `${styles.cardVisible} ${styles.slideFromLeft}` : styles.cardHidden
              }`}
            >
              <p className={styles.serviceDescription}>
                Es un conjunto de derechos y mecanismos diseñados para proteger a las personas que utilizan los
                productos y servicios ofrecidos por entidades financieras. Imagina que eres un cliente de un banco, una
                aseguradora o una cooperativa de ahorro y crédito. La defensa del consumidor financiero asegura que tus
                derechos como usuario sean respetados, que recibas información clara y veraz, y que tengas a dónde
                acudir si surge algún problema o inconformidad. Este marco legal busca equilibrar la relación entre los
                usuarios y las grandes instituciones financieras, que a menudo tienen más información y poder. No se
                trata solo de resolver quejas, sino de prevenir abusos, promover la transparencia y fomentar prácticas
                justas en el sector financiero.
              </p>
            </div>

            {/* SECCIÓN DE ACORDEONES */}
            <div className={styles.accordionSection}>
              {/* ¿Quiénes son los Consumidores Financieros? */}
              <div className={styles.collapsibleSection}>
                <button onClick={() => toggleCollapsible("quienes")} className={styles.collapsibleTrigger}>
                  ¿Quiénes son los Consumidores Financieros?
                  <ChevronDown isOpen={openCollapsibles.quienes} />
                </button>
                {openCollapsibles.quienes && (
                  <div className={styles.collapsibleContent}>
                    <p className={styles.contentParagraph}>
                      Somos todos aquellos que, como clientes o usuarios, utilizamos los productos y servicios de las
                      entidades vigiladas por la Superintendencia Financiera de Colombia. Esto incluye:
                    </p>
                    <ul className={styles.contentList}>
                      <li>
                        <strong>Ahorradores:</strong> Personas con cuentas de ahorro o CDT.
                      </li>
                      <li>
                        <strong>Deudores:</strong> Quienes tienen créditos hipotecarios, de consumo, tarjetas de
                        crédito, etc.
                      </li>
                      <li>
                        <strong>Asegurados:</strong> Aquellos que contratan pólizas de seguros.
                      </li>
                      <li>
                        <strong>Inversionistas:</strong> Personas que compran o venden acciones, bonos u otros
                        instrumentos financieros.
                      </li>
                      <li>
                        <strong>Usuarios de servicios financieros:</strong> Quienes realizan giros, transferencias,
                        pagos, entre otros.
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* ¿Qué Derechos Tienes como Consumidor Financiero? */}
              <div className={styles.collapsibleSection}>
                <button onClick={() => toggleCollapsible("derechos")} className={styles.collapsibleTrigger}>
                  ¿Qué Derechos Tienes como Consumidor Financiero?
                  <ChevronDown isOpen={openCollapsibles.derechos} />
                </button>
                {openCollapsibles.derechos && (
                  <div className={styles.collapsibleContent}>
                    <p className={styles.contentParagraph}>
                      La legislación colombiana, principalmente el Estatuto del Consumidor Financiero, te otorga una
                      serie de derechos fundamentales, entre los que se destacan:
                    </p>
                    <ul className={styles.contentList}>
                      <li>
                        <strong>Recibir información clara y transparente:</strong> Tienes derecho a que te expliquen de
                        forma sencilla y comprensible los productos y servicios, sus costos, riesgos y condiciones.
                      </li>
                      <li>
                        <strong>Elegir libremente:</strong> Puedes escoger el producto o servicio financiero que mejor
                        se adapte a tus necesidades, sin presiones o condicionamientos.
                      </li>
                      <li>
                        <strong>Recibir un trato digno y respetuoso:</strong> Las entidades financieras deben tratarte
                        con cortesía y profesionalismo.
                      </li>
                      <li>
                        <strong>Presentar peticiones, quejas o reclamos:</strong> Tienes el derecho a ser escuchado y a
                        que tus inconformidades sean tramitadas y resueltas de manera oportuna.
                      </li>
                      <li>
                        <strong>Protección contra cláusulas abusivas:</strong> Las condiciones de los contratos deben
                        ser justas y equilibradas.
                      </li>
                      <li>
                        <strong>Educación financiera:</strong> Las entidades deben ofrecerte información que te permita
                        tomar decisiones informadas.
                      </li>
                    </ul>
                  </div>
                )}
              </div>

              {/* ¿Dónde Acudir para Defender tus Derechos? */}
              <div className={styles.collapsibleSection}>
                <button onClick={() => toggleCollapsible("donde")} className={styles.collapsibleTrigger}>
                  ¿Dónde Acudir para Defender tus Derechos?
                  <ChevronDown isOpen={openCollapsibles.donde} />
                </button>
                {openCollapsibles.donde && (
                  <div className={styles.collapsibleContent}>
                    <p className={styles.contentParagraph}>
                      Si sientes que tus derechos como consumidor financiero han sido vulnerados, existen diferentes
                      instancias a las que puedes recurrir a través de nosotros:
                    </p>
                    <ol className={styles.contentOrderedList}>
                      <li className={styles.orderedListItem}>
                        <strong>Atención Directa con la Entidad Financiera:</strong> El primer paso siempre debe ser
                        contactar a la entidad financiera a través de su Defensor del Consumidor Financiero (DCF) o sus
                        canales de atención al cliente. El DCF es una figura independiente dentro de la entidad,
                        encargada de resolver las quejas de los usuarios.
                      </li>
                      <li className={styles.orderedListItem}>
                        <strong>Superintendencia Financiera de Colombia (SFC):</strong> Si no estás satisfecho con la
                        respuesta de la entidad o su DCF, puedes escalar tu queja ante la SFC. Esta es la autoridad de
                        inspección, vigilancia y control del sistema financiero colombiano y tiene la facultad de
                        investigar y sancionar a las entidades que incumplan la normativa.
                      </li>
                      <li className={styles.orderedListItem}>
                        <strong>Procesos Judiciales:</strong> En última instancia, si las vías anteriores no son
                        suficientes, puedes acudir a la justicia ordinaria para buscar la protección de tus derechos.
                      </li>
                    </ol>
                  </div>
                )}
              </div>
            </div>

            {/* Tarjeta 2: Investigación y Recuperación */}
            <div
              className={`${styles.serviceDetail} ${styles.featured} ${styles.portfolioCard} ${styles.dynamicCard} ${
                visiblePortfolioCards.includes(1) ? `${styles.cardVisible} ${styles.slideFromRight}` : styles.cardHidden
              }`}
            >
              <h4 className={styles.serviceTitle}>2. Investigación y Recuperación de Saldos a Favor</h4>
              <p style={{ color: "#ffffff" }} className={styles.serviceDescription}>
                Detallamos el proceso para descubrir y reclamar fondos no reclamados de entidades financieras vigiladas
                por la Superintendencia Financiera de Colombia.
              </p>

              <div className={styles.subService}>
                <h5 className={styles.subServiceTitle}>Proceso de Recuperación</h5>
                <div className={styles.processSteps}>
                  {[
                    {
                      number: "1",
                      title: "Verificación de Calidad",
                      desc: "Confirmamos la calidad del cliente como titular, beneficiario y/o heredero",
                    },
                    {
                      number: "2",
                      title: "Gestiones Legales",
                      desc: "Realizamos las gestiones legales, administrativas y constitucionales pertinentes",
                    },
                    {
                      number: "3",
                      title: "Recuperación Efectiva",
                      desc: "Hacemos efectiva la recuperación de sus saldos a favor",
                    },
                  ].map((step, index) => (
                    <div key={index} className={styles.step}>
                      <div className={styles.stepNumber}>{step.number}</div>
                      <div className={styles.stepContent}>
                        <h6 className={styles.stepTitle}>{step.title}</h6>
                        <p className={styles.stepDescription}>{step.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Áreas de Especialización */}
        <section id="legal-especializacion" className={styles.legalSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Áreas de Especialización</h3>
          </div>
          <div className={styles.specializationGrid}>
            {especialidades.map((area, index) => (
              <div key={index} className={styles.specializationCard}>
                <div className={styles.cardContent}>
                  <h4 className={styles.cardTitle}>{area.title}</h4>
                  <p className={styles.cardDescription}>{area.desc}</p>
                </div>
                <span className={styles.readMore}>Leer más</span>
              </div>
            ))}
          </div>
        </section>

        {/* Cuota Litis en Colombia */}
        <section id="legal-modelo" className={styles.legalSection}>
          <div className={styles.sectionHeader}>
            <h3 className={styles.sectionTitle}>Como Pagan Nuestros Honorarios</h3>
          </div>

          <div className={styles.accordionSection}>
            {/* Acordeón 1: ¿Qué es la Cuota Litis y Cómo Funciona? */}
            <div className={styles.collapsibleSection}>
              <button onClick={() => toggleCollapsible("queEsCuotaLitis")} className={styles.collapsibleTrigger}>
                ¿Qué es la Cuota Litis y Cómo Funciona?
                <ChevronDown isOpen={openCollapsibles.queEsCuotaLitis} />
              </button>
              {openCollapsibles.queEsCuotaLitis && (
                <div className={styles.collapsibleContent}>
                  <p className={styles.contentParagraph}>
                    En el ámbito jurídico colombiano, la cuota litis es una modalidad de pago de honorarios de abogados
                    que se caracteriza por ser un porcentaje del resultado obtenido en un litigio o gestión legal. En
                    términos sencillos, el abogado solo cobra si el cliente gana el caso o logra un beneficio económico,
                    y el monto de sus honorarios depende directamente del valor de lo recuperado o del éxito alcanzado.
                  </p>

                  <p className={styles.highlightBox}>
                    Es un acuerdo de "ganar-ganar" en el que el interés del abogado se alinea directamente con el de su
                    cliente.
                  </p>

                  <h5 className={styles.subTitle}>¿Cómo Funciona la Cuota Litis?</h5>
                  <ol className={styles.contentOrderedList}>
                    <li className={styles.orderedListItem}>
                      <strong>Acuerdo Previo:</strong> Antes de iniciar cualquier gestión o proceso, el abogado y el
                      cliente deben establecer un contrato de prestación de servicios que especifique claramente que los
                      honorarios se pagarán bajo la modalidad de cuota litis. Este contrato debe detallar el porcentaje
                      acordado.
                    </li>
                    <li className={styles.orderedListItem}>
                      <strong>Porcentaje del Resultado:</strong> Si el cliente obtiene un resultado favorable (por
                      ejemplo, gana una demanda y recibe una indemnización, o recupera un saldo a su favor), el abogado
                      recibirá el porcentaje acordado de ese monto.
                    </li>
                    <li>
                      <strong>Sin Resultado, Sin Honorarios (generalmente):</strong> La particularidad de la cuota litis
                      es que, si el caso no se gana o no se obtiene el beneficio económico esperado, el abogado
                      generalmente no cobra honorarios por su trabajo. Esto no exime al cliente de asumir otros gastos
                      del proceso, como costas judiciales, peritajes, o envíos de documentos, que deben ser acordados
                      aparte.
                    </li>
                  </ol>
                </div>
              )}
            </div>

            {/* Acordeón 2: Aspectos Clave de la Cuota Litis */}
            <div className={styles.collapsibleSection}>
              <button onClick={() => toggleCollapsible("aspectosClave")} className={styles.collapsibleTrigger}>
                Aspectos Clave de la Cuota Litis
                <ChevronDown isOpen={openCollapsibles.aspectosClave} />
              </button>
              {openCollapsibles.aspectosClave && (
                <div className={styles.collapsibleContent}>
                  <div className={styles.aspectSection}>
                    <h5 className={styles.subTitle}>Regulación Legal</h5>
                    <p className={styles.contentParagraph}>
                      La cuota litis está regulada en Colombia, y es fundamental que el acuerdo se formalice por
                      escrito. Aunque no existe una tarifa fija, la Corte Suprema de Justicia ha establecido límites y
                      criterios para evitar abusos, buscando un equilibrio entre la justa retribución del abogado y la
                      protección del cliente. Se ha considerado que un porcentaje superior al 50% de lo obtenido puede
                      ser desproporcionado.
                    </p>
                  </div>

                  <div className={styles.aspectSection}>
                    <h5 className={styles.subTitle}>Ventajas para el Cliente</h5>
                    <ul className={styles.contentList}>
                      <li>
                        <strong>Acceso a la justicia:</strong> Permite a personas con recursos limitados acceder a
                        servicios legales, ya que no necesitan pagar sumas iniciales elevadas.
                      </li>
                      <li>
                        <strong>Motivación del abogado:</strong> El abogado tiene un fuerte incentivo para obtener el
                        mejor resultado posible, ya que sus honorarios dependen de ello.
                      </li>
                      <li>
                        <strong>Menor riesgo financiero inicial:</strong> El cliente no asume un costo fijo por
                        adelantado por los honorarios del abogado.
                      </li>
                    </ul>
                  </div>

                  <div className={styles.aspectSection}>
                    <h5 className={styles.subTitle}>Consideraciones Importantes</h5>
                    <ul className={styles.contentList}>
                      <li>
                        <strong>Transparencia:</strong> Es crucial que el porcentaje y las condiciones de la cuota litis
                        sean completamente transparentes y acordadas por ambas partes desde el inicio.
                      </li>
                      <li>
                        <strong>Gastos adicionales:</strong> Como se mencionó, la cuota litis cubre los honorarios del
                        abogado, no los gastos del proceso (copias, notificaciones, peritajes, etc.). Estos deben ser
                        acordados de manera independiente.
                      </li>
                      <li>
                        <strong>Casos de Éxito:</strong> Esta modalidad es comúnmente utilizada en casos donde hay una
                        expectativa de recuperación económica, como demandas por indemnización, cobro de deudas,
                        procesos de sucesión o reclamaciones de saldos a favor.
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>

            {/* Acordeón 3: Nuestro Compromiso en DYD Asesorías */}
            <div className={styles.collapsibleSection}>
              <button
                onClick={() => toggleCollapsible("nuestroCompromiso")}
                className={`${styles.collapsibleTrigger} ${styles.commitmentTrigger}`}
              >
                Nuestro Compromiso en Defensa Financiera S.A.S.
                <ChevronDown isOpen={openCollapsibles.nuestroCompromiso} />
              </button>
              {openCollapsibles.nuestroCompromiso && (
                <div className={`${styles.collapsibleContent} ${styles.commitmentContent}`}>
                  <h4 className={styles.commitmentTitle}>Nuestro Compromiso Contigo</h4>
                  <p className={styles.commitmentDescription}>
                    En <strong>DEFENSA FINANCIERA S.A.S.</strong>, utilizamos la modalidad de cuota litis para la
                    recuperación de saldos a favor de nuestros clientes, garantizando que solo pagas un porcentaje sobre
                    el valor efectivamente recuperado.
                  </p>
                  <div className={styles.commitmentBox}>
                    <p className={styles.commitmentQuote}>
                      Alineamos nuestro éxito con el tuyo: <br />
                      <em>"Solo ganamos cuando tú ganas"</em>
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  )
}

export default ServiciosLegalesSection